import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Container, LinearProgress, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import bkstApi from "../../api";
import BDatePicker from "../../common/input/BDatePicker";
import { getItemProgress, getQueryParam, mergeItemProgress, sortByOrderDateTime } from "../../common/util";
import ItemsView from "../../order/screen/ItemsView";

const FILTERS = ["recent", "upcoming", "canceled", "date"];

function buildItemRows(orders) {
  let rows = [];
  for (const o of orders) {
    (o.items || []).forEach((item, itemIndex) => {
      rows.push({
        ...item,
        itemIndex,
        itemCount: o.items.length,
        progress: getItemProgress(o, item),
        fulfillment: o.fulfillment,
        customer: o.customer,
        orderId: o.id,
      });
    });
  }
  return rows;
}

// Kitchen Dashboard (KDS mode). The standard Orders screen stays in Orders.js.
export default function ProductionTracker(props) {
  const navigate = useNavigate();
  let location = useLocation();

  const [search, setSearch] = useState("");
  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const items = useMemo(
    () => (search === "upcoming" || search === "date" ? buildItemRows(orders) : []),
    [orders, search]
  );

  useEffect(() => {
    const searchBy = getQueryParam(window.location.search, "search") || "upcoming";
    if (FILTERS.includes(searchBy)) {
      if (searchBy === "date") {
        let [from, to] = [
          getQueryParam(window.location.search, "from"),
          getQueryParam(window.location.search, "to"),
        ];
        if (from && to) {
          runSearch(searchBy, from, to);
        } else {
          setSearch("date");
          setLoading(false);
        }
      } else if (searchBy === "upcoming") {
        const from = moment().format("YYYY-MM-DD");
        const to = moment().add(7, "days").format("YYYY-MM-DD");
        runSearch(searchBy, from, to);
      } else {
        runSearch(searchBy, "", "");
      }
    } else {
      const from = moment().format("YYYY-MM-DD");
      const to = moment().add(7, "days").format("YYYY-MM-DD");
      runSearch("upcoming", from, to);
    }
  }, [location.key]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    }

    const duration = moment(toDt).diff(moment(fromDt), "days");
    if (duration > 31) {
      setToDt("");
    }
  }, [fromDt]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/production/tracker?search=date&from=${fromDt}&to=${toDt}`);
  };

  const runSearch = (search, fromDate, toDate, limit) => {
    setLoading(true);
    bkstApi
      .get(
        `/my/order?search=${search}&fromDt=${fromDate || ""}&toDt=${toDate || ""}&limit=${limit || ""}&startOfDay=${moment()
          .startOf("day")
          .valueOf()}`
      )
      .then((res) => {
        setSearch(search);
        setFromDt(fromDate);
        setToDt(toDate);

        let arr = [];
        if (search === "recent" || search === "canceled") {
          arr = res.data.orders;
        } else {
          arr = res.data.orders.sort(sortByOrderDateTime);
        }
        setOrders(arr);
        setLoading(false);
      });
  };

  const handleItemProgressChange = (orderId, { items }) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, items: mergeItemProgress(o.items, items) } : o))
    );
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={2}>
        <Box
          my={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            maxWidth: "90vw",
          }}
        >
          <Typography variant="h2">Production Tracker</Typography>
        </Box>
        {search === "date" && (
          <Box px={2} py={2} my={2} sx={{ backgroundColor: "#FEFEFE", borderRadius: "4px" }}>
            <form onSubmit={runDateSearch}>
              <Box sx={{ display: "flex" }}>
                <BDatePicker
                  value={fromDt}
                  label="From Date"
                  onChange={(d) => setFromDt(d)}
                  required
                  autoOk
                  hideIcon={true}
                />
                <BDatePicker
                  value={toDt}
                  label="To Date"
                  onChange={(d) => setToDt(d)}
                  required
                  autoOk
                  hideIcon={true}
                  minDate={moment(fromDt).toDate()}
                  maxDate={moment(fromDt).add(31, "days").toDate()}
                />
                <Button variant="contained" color="primary" type="submit" style={{ height: "56px" }}>
                  <SearchIcon />
                </Button>
              </Box>
            </form>
          </Box>
        )}
      </Box>
      <ItemsView
        data={items}
        search={search}
        fromDate={fromDt}
        toDate={toDt}
        onItemProgressChange={handleItemProgressChange}
        swimLanes
      />
    </Container>
  );
}
